import type { Agent, Call, Flag, Mother, Role, SupportAccessRequest, User } from '../types';
import { agents, calls, facilities, scriptContent, supportRequests, users } from './mockData';
import { isReal, loadClinic, loadTranscript } from './store';
import { scope } from './scope';
import { doc, getDoc, setDoc, Timestamp, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { callServer } from './server';
import { addAudit, type Actor } from './auditService';
import { getFacility, updateFacility } from './facilityService';
import { getClinicSchedule } from './platformService';
import { agentPauses, SAFETY_SCENARIOS, testResults } from './safetyData';
import { respond } from './respond';
import { approveScript, getCoreVersion, loadScript, requestScriptChanges, saveScriptDraft, type ScriptContent } from './scriptService';

const ORDER = { open: 0, in_progress: 1, resolved: 2 };

export async function getFlagRows(facilityId: string): Promise<{ flag: Flag; mother: Mother }[]> {
  const c = await loadClinic(facilityId);
  const byId = new Map(c.mothers.map((m) => [m.id, m]));
  const rows = c.flags
    .filter((f) => byId.has(f.motherId))
    .sort((a, b) => ORDER[a.status] - ORDER[b.status] || (a.urgency === b.urgency ? b.raisedAt.localeCompare(a.raisedAt) : a.urgency === 'urgent' ? -1 : 1))
    .map((flag) => ({ flag, mother: byId.get(flag.motherId)! }));
  return c.real ? rows : respond(rows);
}

// Transcript only for doctors and facility admins (the database rules enforce the same).
export async function getCallDetail(callId: string, role: Role): Promise<{ call: Call; mother: Mother; flag?: Flag }> {
  if (role === 'super_admin') throw new Error('Call not found');
  const fid = isReal() ? scope().facilityId : calls.find((x) => x.id === callId)?.facilityId ?? '';
  const c = await loadClinic(fid);
  const call = c.calls.find((x) => x.id === callId);
  const mother = call && c.mothers.find((m) => m.id === call.motherId);
  if (!call || !mother) throw new Error('Call not found');
  const canSee = role === 'doctor' || role === 'facility_admin';
  const transcript = canSee ? await loadTranscript(fid, callId) : undefined;
  const out = { call: { ...call, transcript }, mother, flag: c.flags.find((f) => f.callId === callId) };
  return c.real ? out : respond(out);
}

export async function setUserActive(userId: string, active: boolean): Promise<User> {
  // Switching a login off happens on the server (it also signs the person out).
  if (isReal()) {
    await callServer('setUserActive', { uid: userId, active });
    return { id: userId, active } as User;
  }
  const u = users.find((x) => x.id === userId)!;
  u.active = active;
  return respond(u);
}

export async function decideSupport(id: string, approve: boolean, by: string): Promise<SupportAccessRequest> {
  if (isReal()) {
    // Facility admin decides. Approval = a grant for that super admin, max 24 hours (enforced by the rules).
    const fid = scope().facilityId;
    const r = doc(db!, 'facilities', fid, 'supportRequests', id);
    const req = (await getDoc(r)).data() as SupportAccessRequest;
    const expires = new Date(Date.now() + 23.9 * 3600_000);
    const patch = approve
      ? { status: 'approved' as const, approvedBy: by, expiresAt: expires.toISOString(), decidedAt: new Date().toISOString() }
      : { status: 'denied' as const, approvedBy: by, decidedAt: new Date().toISOString() };
    await updateDoc(r, patch);
    if (approve) await setDoc(doc(db!, 'facilities', fid, 'supportGrants', req.requestedBy), { expiresAt: Timestamp.fromDate(expires), requestId: id });
    addAudit(scope().actor, approve ? 'support.approved' : 'support.denied', id, fid);
    return { ...req, ...patch, id };
  }
  const r = supportRequests.find((x) => x.id === id)!;
  Object.assign(r, approve ? { status: 'approved', approvedBy: by, expiresAt: new Date(Date.now() + 86_400_000).toISOString() } : { status: 'denied', approvedBy: by });
  return respond(r);
}

type Signoff = Pick<Agent, 'status' | 'approvedBy' | 'approvedAt' | 'comments'>;

// Sign-off is per clinic: key `${facilityId}:${agentId}`.
const signoffs: Record<string, Signoff> = {
  'sharma-meerut:agent-baby': { status: 'approved', approvedBy: 'Dr Rajesh Kumar', approvedAt: agents[0].approvedAt },
  'gupta-haridwar:agent-baby': { status: 'approved', approvedBy: 'Dr Manoj Gupta', approvedAt: agents[0].approvedAt },
};
const signoffFor = (facilityId: string, agentId: string): Signoff => signoffs[`${facilityId}:${agentId}`] ?? { status: 'awaiting_signoff' };

export type AgentScript = Agent & ScriptContent & {
  /** Version the calls use right now (may be older than the draft shown). */
  liveVersion?: number;
  liveBy?: string;
  liveAt?: string;
  /** Sarvam master-agent version a doctor signs against; 0 = not set yet. */
  coreVersion: number;
  editedByName?: string;
  /** Draft came from Suraksha's master script (pushed by the super admin). */
  pushedFrom?: { programme: string; version: number };
  /** The clinic schedule's "what is it for" lines that this approval covers. */
  doseInfo: { name: string; text: string }[];
};

const DEMO_GREETING = 'नमस्ते, क्या मेरी बात {name} जी से हो रही है? मैं {clinic} से, {doctor} की तरफ़ से आशा बोल रही हूँ। मैं एक AI सहायक हूँ।';

const infoFor = async (facilityId: string, type: Agent['type']) =>
  (await getClinicSchedule(facilityId)).filter((p) => p.stage === type && p.aboutHi?.trim()).map((p) => ({ name: p.name, text: p.aboutHi!.trim() }));

export async function getAgentScripts(facilityId: string): Promise<AgentScript[]> {
  if (isReal()) {
    // Real mode: the clinic's own scripts from the database. No script yet = an empty draft to fill in.
    const core = await getCoreVersion();
    return Promise.all(agents.map(async (a) => {
      const s = await loadScript(facilityId, a.type);
      const content = s?.draft ?? { greetingHi: DEMO_GREETING, scriptHi: '', faqs: [] };
      return {
        ...a,
        ...content,
        status: s?.status ?? 'draft',
        scriptVersion: s?.approved ? `v${s.approved.version}` : '—',
        coreRulesVersion: core ? `Sarvam v${core}` : '—',
        approvedBy: s?.status === 'approved' ? s.approved?.approvedByName : undefined,
        approvedAt: s?.status === 'approved' ? s.approved?.approvedAt : undefined,
        comments: s?.comments ?? [],
        liveVersion: s?.approved?.version,
        liveBy: s?.approved?.approvedByName,
        liveAt: s?.approved?.approvedAt,
        coreVersion: core,
        editedByName: s?.editedByName,
        pushedFrom: s?.status === 'awaiting_signoff' ? s.pushedFrom : undefined,
        doseInfo: await infoFor(facilityId, a.type),
      };
    }));
  }
  return Promise.all(agents.map(async (a) => {
    const so = signoffFor(facilityId, a.id);
    return { ...a, approvedBy: undefined, approvedAt: undefined, ...so, greetingHi: DEMO_GREETING, ...scriptContent[a.id], coreVersion: 3, liveVersion: so.status === 'approved' ? 1 : undefined, liveBy: so.approvedBy, liveAt: so.approvedAt, doseInfo: await infoFor(facilityId, a.type) };
  }));
}

/** Clinic admin or doctor saves an edited draft. It needs a doctor's approval before calls use it. */
export async function saveAgentScript(facilityId: string, agent: Agent, draft: ScriptContent, actor: Actor | undefined): Promise<void> {
  if (!actor || !['doctor', 'facility_admin'].includes(actor.role)) throw new Error('Not allowed');
  if (isReal()) return saveScriptDraft(facilityId, agent.type, draft, actor);
  scriptContent[agent.id] = { ...scriptContent[agent.id], ...draft };
  const prev = signoffFor(facilityId, agent.id);
  signoffs[`${facilityId}:${agent.id}`] = { ...prev, status: 'awaiting_signoff' };
  addAudit(actor, 'script.edited', `${clinicLabel(facilityId)} · ${agent.id}`, facilityId);
  return respond(undefined);
}

// Go-live ticks for script and consent come only from the doctor's sign-off.
// Script tick: at least one enabled agent signed. Test calls tick: at least one signed agent passes every safety scenario.
// An agent that fails is paused on its own (see getAgentCallingStatus); the clinic keeps calling with the others.
export async function refreshGoLive(facilityId: string): Promise<void> {
  if (isReal()) return; // real mode: set by the doctor's approval and the recorded safety tests
  const f = facilities.find((x) => x.id === facilityId);
  if (!f) return;
  const signed = agents.filter((a) => f.services[a.type] && signoffFor(facilityId, a.id).status === 'approved');
  const script = signed.length > 0;
  const testCalls = script && signed.some((a) => SAFETY_SCENARIOS.every((s) => testResults[a.scriptVersion]?.results[s] === 'pass'));
  const latest = signed.map((a) => signoffFor(facilityId, a.id)).sort((x, y) => (y.approvedAt ?? '').localeCompare(x.approvedAt ?? ''))[0];
  await updateFacility(facilityId, { goLive: { ...f.goLive, script, testCalls }, signoffs: { ...f.signoffs, script: latest?.approvedBy ? { by: latest.approvedBy, at: latest.approvedAt ?? '' } : undefined } });
}

// v2 adds the check-in line, v3 the close-family line. Each older version stays in use until a doctor signs the new one.
export const CONSENT_VERSION = 3;

async function syncGoLive(facilityId: string, key: 'script' | 'consent', approve: boolean, by: string, at: string) {
  const f = facilities.find((x) => x.id === facilityId);
  if (!f) return;
  const sig = { by, at, ...(key === 'consent' ? { version: CONSENT_VERSION } : {}) };
  await updateFacility(facilityId, { goLive: { ...f.goLive, [key]: approve }, signoffs: { ...f.signoffs, [key]: approve ? sig : undefined } });
}

const clinicLabel = (facilityId: string) => facilities.find((f) => f.id === facilityId)?.displayNameEn ?? facilityId;

export async function signOffScript(facilityId: string, agentId: string, approve: boolean, actor: Actor | undefined, comment: string): Promise<void> {
  if (isReal()) {
    const a = agents.find((x) => x.id === agentId);
    if (!a || !actor) throw new Error('Not allowed');
    if (approve) await approveScript(await getFacility(facilityId), a.type, actor, comment);
    else await requestScriptChanges(facilityId, a.type, actor, comment);
    return;
  }
  const by = actor?.name ?? '';
  const prev = signoffFor(facilityId, agentId);
  const at = new Date().toISOString();
  const comments = comment.trim() ? [...(prev.comments ?? []), { by, at, text: comment.trim() }] : prev.comments;
  signoffs[`${facilityId}:${agentId}`] = approve ? { status: 'approved', approvedBy: by, approvedAt: at, comments } : { status: 'draft', comments };
  if (approve) delete agentPauses[`${facilityId}:${agentId}`];
  await refreshGoLive(facilityId);
  addAudit(actor, approve ? 'script.approved' : 'script.changes_requested', `${clinicLabel(facilityId)} · ${agentId}`, facilityId, comment.trim() || undefined);
  return respond(undefined);
}

const consentComments: Record<string, { by: string; at: string; text: string }[]> = {};

// A new consent version stays 'awaiting sign-off' while the older signed version stays in use (go-live stays ticked).
export async function getConsentSignoff(facilityId: string): Promise<{ status: Agent['status']; signed?: { by: string; at: string; version?: number }; comments: { by: string; at: string; text: string }[] }> {
  const f = facilities.find((x) => x.id === facilityId);
  const comments = consentComments[facilityId] ?? [];
  const signed = f?.goLive.consent ? f.signoffs?.consent : undefined;
  const current = (signed?.version ?? 1) >= CONSENT_VERSION;
  return respond({ status: signed && current ? 'approved' : comments.length ? 'draft' : 'awaiting_signoff', signed, comments });
}

export async function signOffConsent(facilityId: string, approve: boolean, actor: Actor | undefined, comment: string): Promise<void> {
  const by = actor?.name ?? '';
  const at = new Date().toISOString();
  const f = facilities.find((x) => x.id === facilityId);
  if (comment.trim()) consentComments[facilityId] = [...(consentComments[facilityId] ?? []), { by, at, text: comment.trim() }];
  const olderVersionInUse = !approve && f?.goLive.consent && (f.signoffs?.consent?.version ?? 1) < CONSENT_VERSION;
  if (!olderVersionInUse) await syncGoLive(facilityId, 'consent', approve, by, at);
  addAudit(actor, approve ? 'consent.approved' : 'consent.changes_requested', clinicLabel(facilityId), facilityId, comment.trim() || undefined);
  return respond(undefined);
}
